import { LoadingSpinner } from "@shared/components/LoadingSpinner";
import AuthenticatedLayout from "@shared/layouts/AuthenticatedLayout";
import theme from "@shared/utils/themeColors";
import { useEffect, useState } from "react";
import { CreateSpaceModal } from "../components/CreateSpaceModal";
import SpaceEntries from "../components/SpaceEntries";
import SpaceLeftContent from "../components/SpaceLeftContent";
import SpaceRightContent from "../components/SpaceRightContent";
import { useSpacesStore } from "../store/useSpacesStore";
import { SortType, Space } from "../types/space.types";

export default function SpacesPage() {
  let loaded = false;
  useEffect(() => {
    if (!loaded) {
      fetchSpaces(1);
      loaded = true;
    }
    return () => {
      useSpacesStore.getState().resetState();
    };
  }, []);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [pinnedSpaces, setPinnedSpaces] = useState<Space[]>([]);
  const {
    spaces,
    totalSpaces,
    currentPage,
    hasNextPage,
    isLoading,
    error,
    sortType,
    fetchSpaces,
    setSortType,
  } = useSpacesStore();

  useEffect(() => {
    const pinned = spaces.filter((s) => s.isPinned);
    if (pinned.length > 0) {
      setPinnedSpaces(pinned);
    }
  }, [spaces]);

  const handlePageChange = (page: number) => {
    fetchSpaces(page);
  };

  const handleSortChange = (newSortType: SortType) => {
    if (newSortType !== sortType) {
      setSortType(newSortType);
    }
  };
  return (
    <AuthenticatedLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8 md:py-0 w-full max-w-9xl mx-auto">
        <div className="xl:flex">
          {/* Left + Middle content */}
          <div className="md:flex flex-1">
            {/* Left content */}
            <SpaceLeftContent
              onCreateSpace={() => setIsCreateModalOpen(true)}
            />

            {/* Middle content */}
            <div className="flex-1 md:ml-8 xl:mx-4 2xl:mx-8">
              <div className="md:py-8">
                {/* Pinned Space Entries (above sort buttons) */}
                {!error && pinnedSpaces.length > 0 && (
                  <div className="space-y-2 mb-4">
                    <SpaceEntries spaces={pinnedSpaces} />
                  </div>
                )}

                {/* Buttons group */}
                <div className="mb-4">
                  <div className="w-full flex flex-wrap -space-x-px">
                    <button
                      className={`btn grow bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 ${
                        sortType === SortType.POPULAR
                          ? theme.text.brand
                          : `${theme.text.secondary} hover:bg-gray-50 dark:hover:bg-gray-700/20`
                      } rounded-none first:rounded-l-lg last:rounded-r-lg`}
                      onClick={() => handleSortChange(SortType.POPULAR)}
                    >
                      Popular
                    </button>
                    <button
                      className={`btn grow bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 ${
                        sortType === SortType.NEWEST
                          ? theme.text.brand
                          : `${theme.text.secondary} hover:bg-gray-50 dark:hover:bg-gray-700/20`
                      } rounded-none first:rounded-l-lg last:rounded-r-lg`}
                      onClick={() => handleSortChange(SortType.NEWEST)}
                    >
                      Newest
                    </button>
                    <button
                      className={`btn grow bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 ${
                        sortType === SortType.FOLLOWING
                          ? theme.text.brand
                          : `${theme.text.secondary} hover:bg-gray-50 dark:hover:bg-gray-700/20`
                      } rounded-none first:rounded-l-lg last:rounded-r-lg`}
                      onClick={() => handleSortChange(SortType.FOLLOWING)}
                    >
                      Following
                    </button>
                  </div>
                </div>

                {/* Space Entries */}
                <div className="space-y-2">
                  {isLoading ? (
                    <div className="flex justify-center py-8">
                      <LoadingSpinner />
                    </div>
                  ) : error ? (
                    <div className="text-center py-8 text-red-500">
                      <p>{error}</p>
                      <button
                        className={`btn-sm bg-white border-gray-200 hover:border-gray-300 ${theme.text.brand} mt-2`}
                        onClick={() => fetchSpaces(currentPage)}
                      >
                        Try Again
                      </button>
                    </div>
                  ) : spaces.filter((s) => !s.isPinned).length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <p>No spaces found</p>
                    </div>
                  ) : (
                    <SpaceEntries spaces={spaces.filter((s) => !s.isPinned)} />
                  )}
                </div>

                {/* Pagination */}
                {!isLoading && spaces.length > 0 && (
                  <div className="mt-6 text-right">
                    <nav
                      className="inline-flex"
                      role="navigation"
                      aria-label="Navigation"
                    >
                      <ul className="flex justify-center">
                        <li className="ml-3 first:ml-0">
                          {currentPage > 1 ? (
                            <button
                              className="btn bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300"
                              onClick={() => handlePageChange(currentPage - 1)}
                            >
                              ‹‹ Previous
                            </button>
                          ) : (
                            <span className="btn bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 text-gray-300 dark:text-gray-600">
                              ‹‹ Previous
                            </span>
                          )}
                        </li>
                        <li className="ml-3 first:ml-0">
                          {hasNextPage ? (
                            <button
                              className="btn bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300"
                              onClick={() => handlePageChange(currentPage + 1)}
                            >
                              Next ››
                            </button>
                          ) : (
                            <span className="btn bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 text-gray-300 dark:text-gray-600">
                              Next ››
                            </span>
                          )}
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right content */}
          <SpaceRightContent
            onCreateSpace={() => setIsCreateModalOpen(true)}
            hideAuthorInfo={true}
          />
        </div>
      </div>

      {/* Create Space Modal */}
      <CreateSpaceModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </AuthenticatedLayout>
  );
}
